import { Transforms, Editor, Range, Node, Element, Text, Path } from 'slate';
import { RenderElementProps } from 'slate-react';
import { CustomEditor, CustomElement } from 'slate/types/slate';
import { isStyledParagraph } from './utils/isStyledParagraph';
import { StyledParagraphElement } from './components/StyledParagraphElement';

const withStyledParagraph =
  (options: { isPDF?: boolean } = {}) =>
  (editor: CustomEditor) => {
    const { insertBreak, renderElement, isBlock } = editor;
    const { isPDF } = options;
    editor.isBlock = (element: CustomElement) => isStyledParagraph(element) || isBlock(element);

    editor.renderElement = (props: RenderElementProps) => {
      if (isStyledParagraph(props.element)) {
        return <StyledParagraphElement {...props} element={props.element} isPDF={isPDF} />;
      } else {
        return renderElement(props);
      }
    };

    editor.insertBreak = () => {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) {
        return insertBreak();
      }
      const [itemEntry] = Editor.nodes(editor, {
        match: isStyledParagraph,
      });
      if (!itemEntry) {
        return insertBreak();
      }
      const [styledParagraphNode, styledParagraphPath] = itemEntry;

      const currentLinePath = selection.anchor.path;
      const relativePath = currentLinePath.slice(styledParagraphPath.length);

      const currentLineNode = relativePath.reduce<Node | null>(
        (node, idx) => (node && Element.isElement(node) ? node.children[idx] : null),
        styledParagraphNode
      );
      const isCurrentLineEmpty = currentLineNode && Text.isText(currentLineNode) && currentLineNode.text === '';

      if (isCurrentLineEmpty) {
        return Editor.withoutNormalizing(editor, () => {
          const parentPath = Path.parent(styledParagraphPath);
          const parentNode = Node.get(editor, parentPath);
          const insertIndex = styledParagraphPath[styledParagraphPath.length - 1] + 1;
          const insertPath = [...parentPath, insertIndex];
          if ('children' in parentNode && insertIndex <= parentNode.children.length) {
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: insertPath });
            Transforms.select(editor, Editor.start(editor, insertPath));
          }
        });
      }
      return insertBreak();
    };

    return editor;
  };

export default withStyledParagraph;
